import { Dexie, type EntityTable } from 'dexie';

export interface JsonRecord {
  key: string;
  data: unknown;
  updatedAt: number;
  expiresAt?: number;
}

export type ResourceType = 'image' | 'video' | 'audio' | 'other';

export interface ResourceRecord {
  key: string;
  type: ResourceType;
  blob: Blob;
  mimeType?: string;
  size?: number;
  updatedAt: number;
  expiresAt?: number;
}

export const db = new Dexie('MiutaLocalDB') as Dexie & {
  json_store: EntityTable<JsonRecord, 'key'>;
  resource_store: EntityTable<ResourceRecord, 'key'>;
};

db.version(1).stores({
  // 主键使用 key，索引包含过期和更新时间，便于清理与排序
  json_store: 'key, expiresAt, updatedAt',
  resource_store: 'key, type, expiresAt, updatedAt',
});

export class JsonStore {
  private static instance: JsonStore;
  private constructor() { }

  public static getInstance(): JsonStore {
    if (!JsonStore.instance) {
      JsonStore.instance = new JsonStore();
    }
    return JsonStore.instance;
  }

  // 仅暴露 set
  async set(key: string, data: unknown, ttlMs?: number): Promise<void> {
    try {
      const now = Date.now();
      const record: JsonRecord = {
        key,
        data,
        updatedAt: now,
        expiresAt: ttlMs ? now + ttlMs : undefined,
      };
      await db.json_store.put(record);
    } catch (error) {
      console.error("JsonStore set failed:", error.message);
      // throw error;
    }
  }

  // 仅暴露 get
  async get<T = unknown>(key: string): Promise<T | null> {
    try {
      const record = await db.json_store.get(key);
      if (!record) return null;
      // 过期自动清理
      if (record.expiresAt && record.expiresAt <= Date.now()) {
        await db.json_store.delete(key);
        return null;
      }
      return record.data as T;
    } catch (error) {
      console.error("JsonStore get failed:", error.message);
      // throw error;
    }
  }
}

export const jsonStore = JsonStore.getInstance();

function toBlob(input: Blob | ArrayBuffer, mimeType?: string): Blob {
  if (input instanceof Blob) return input;
  if (input instanceof ArrayBuffer) return new Blob([input], { type: mimeType || 'application/octet-stream' });
  return new Blob([input], { type: mimeType || 'application/octet-stream' });
}

export class ResourceStore {
  private static instance: ResourceStore;
  private constructor() { }

  public static getInstance(): ResourceStore {
    if (!ResourceStore.instance) {
      ResourceStore.instance = new ResourceStore();
    }
    return ResourceStore.instance;
  }

  // 仅暴露 set
  async set(
    key: string,
    resource: Blob | ArrayBuffer,
    options?: { type?: ResourceType; mimeType?: string; ttlMs?: number }
  ): Promise<void> {
    const now = Date.now();
    const blob = toBlob(resource, options?.mimeType);
    const record: ResourceRecord = {
      key,
      type: options?.type || 'other',
      blob,
      mimeType: options?.mimeType,
      size: blob.size,
      updatedAt: now,
      expiresAt: options?.ttlMs ? now + options.ttlMs : undefined,
    };
    await db.resource_store.put(record);
  }

  // 仅暴露 get（返回 Blob）
  async get(key: string): Promise<Blob | null> {
    const record = await db.resource_store.get(key);
    if (!record) return null;
    // 过期自动清理
    if (record.expiresAt && record.expiresAt <= Date.now()) {
      await db.resource_store.delete(key);
      return null;
    }
    return record.blob;
  }
}

export const resourceStore = ResourceStore.getInstance();
